/**
 * New Registration Modal Component
 *
 * Modal popup for registering new employees.
 * Includes:
 * - Form with input fields (name, gender, contact number, email, optional 担当者/現場名).
 *   Password is system-generated when invitation is sent.
 * - Save and Clear buttons
 * - Table showing registered persons with edit/delete options
 */
import React, { useState, useEffect } from "react";
import "./NewRegistrationModal.css";
import editIcon from "../../../../assests/icons/edit_icon.png";
import eraserIcon from "../../../../assests/icons/eraser_icon.png";
import addNewIcon from "../../../../assests/icons/addNew_icon.png";
import { API_BASE_URL } from "../../../../config";
import { deleteRegisteredEmployee } from "../../../../services/employeeDeleteApi";
import { adminFetch } from "../../../../utils/adminFetch";

// Email: basic format local@domain.tld
const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

// Japanese phone: 10 or 11 digits, optionally with hyphens (e.g. 090-1234-5678, 03-1234-5678)
const isValidJapanesePhone = (value: string): boolean => {
  const digitsOnly = value.replace(/\D/g, "");
  return (
    /^0\d{9,10}$/.test(digitsOnly) &&
    (digitsOnly.length === 10 || digitsOnly.length === 11)
  );
};

const normalizePhoneDigits = (value: string): string => value.replace(/\D/g, "");

const emptyForm = () => ({
  name: "",
  gender: "",
  contactNumber: "",
  email: "",
  password: "",
  tantosha: "",
  genba: "",
});

export interface RegisteredPerson {
  id: string;
  name: string;
  gender: string;
  contactNumber: string;
  email: string;
  password: string;
  tantosha: string;
  genba: string;
}

export interface NewRegistrationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback when a person is saved */
  onSave?: (person: RegisteredPerson) => void;
  /** Callback when a person is updated */
  onUpdate?: (person: RegisteredPerson) => void;
  /** Callback when a person is deleted */
  onDelete?: (id: string) => void;
}

const NewRegistrationModal: React.FC<NewRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onUpdate,
  onDelete,
}) => {
  const [formData, setFormData] = useState(emptyForm);

  const [registeredPersons, setRegisteredPersons] = useState< RegisteredPerson[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** After reset-password API success, the new plain password to show and copy */
  const [resetPasswordResult, setResetPasswordResult] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const loadEmployees = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/employees`);
        if (res.ok) {
          const data = await res.json();
          setRegisteredPersons(
            data.map(
              (emp: {
                emailaddress: string;
                name: string;
                gender: string;
                contactnumber: string;
                status?: string;
                tantosha?: string | null;
                genba?: string | null;
              }) => ({
                id: emp.emailaddress,
                name: emp.name,
                gender: emp.gender,
                contactNumber: String(emp.contactnumber ?? ""),
                email: emp.emailaddress,
                status: emp.status ?? "active",
                password: "",
                tantosha: emp.tantosha ?? "",
                genba: emp.genba ?? "",
              }),
            ),
          );
        } else {
          setRegisteredPersons([]);
        }
      } catch (err) {
        setRegisteredPersons([]);
      }
    };
    loadEmployees();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const optionalText = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  };

  const handleSave = async () => {
    if (
      !formData.name ||
      !formData.gender ||
      !formData.contactNumber ||
      !formData.email
    ) {
      alert("氏名・性別・連絡先番号・メールアドレスを入力してください");
      return;
    }

    if (!isValidEmail(formData.email)) {
      alert("有効なメールアドレスを入力してください");
      return;
    }
    if (!isValidJapanesePhone(formData.contactNumber)) {
      alert("有効な電話番号を入力してください（0から始まる10桁または11桁）");
      return;
    }
    if (isSubmitting) return;
    const contactnumber = normalizePhoneDigits(formData.contactNumber);
    const tantosha = optionalText(formData.tantosha);
    const genba = optionalText(formData.genba);
    setIsSubmitting(true);
    try {
      if (editingId) {
        const body: Record<string, unknown> = {
          name: formData.name,
          gender: formData.gender,
          contactnumber,
          tantosha,
          genba,
        };
        const res = await adminFetch(
          `${API_BASE_URL}/api/employees/${encodeURIComponent(editingId)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
        );
        if (res.ok) {
          const updatedPerson: RegisteredPerson = {
            id: editingId,
            name: formData.name,
            gender: formData.gender,
            contactNumber: formData.contactNumber,
            email: formData.email,
            password: "",
            tantosha: tantosha ?? "",
            genba: genba ?? "",
          };
          setRegisteredPersons((prev) =>
            prev.map((p) => (p.id === editingId ? updatedPerson : p)),
          );
          if (onUpdate) onUpdate(updatedPerson);
          setEditingId(null);
          setResetPasswordResult(null);
          setFormData(emptyForm());
        } else {
          alert("更新に失敗しました");
        }
      } else {
        const res = await adminFetch(`${API_BASE_URL}/api/employees`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            emailaddress: formData.email.trim(),
            gender: formData.gender,
            contactnumber,
            tantosha,
            genba,
            accountstatus: "registered",
          }),
        });
        if (res.ok) {
          const saved = await res.json();
          const newPerson: RegisteredPerson = {
            id: saved.emailaddress ?? Date.now().toString(),
            name: formData.name,
            gender: formData.gender,
            contactNumber: contactnumber,
            email: formData.email.trim(),
            password: "",
            tantosha: tantosha ?? "",
            genba: genba ?? "",
          };
          setRegisteredPersons((prev) => [...prev, newPerson]);
          if (onSave) onSave(newPerson);
          setFormData(emptyForm());
        } else if (res.status === 409) {
          alert("このメールアドレスは既に登録されています");
        } else if (res.status === 400) {
          alert("入力内容が正しくありません。電話番号や必須項目を確認してください");
        } else {
          alert("登録に失敗しました");
        }
      }
    } catch (err) {
      alert("サーバーに接続できません");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClear = () => {
    setFormData(emptyForm());
    setEditingId(null);
    setResetPasswordResult(null);
  };

  const handleResetPassword = async () => {
    if (!editingId) return;
    setIsResettingPassword(true);
    setResetPasswordResult(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/employees/${encodeURIComponent(editingId)}/reset-password`,
        { method: "POST" },
      );
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const newPassword = typeof data?.password === "string" ? data.password : "";
        if (newPassword) {
          setResetPasswordResult(newPassword);
        } else {
          alert("パスワードの取得に失敗しました。");
        }
      } else {
        const msg = res.status === 404
          ? "該当する従業員が見つかりません。"
          : "パスワードのリセットに失敗しました。";
        alert(msg);
      }
    } catch {
      alert("サーバーに接続できません");
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!resetPasswordResult) return;
    try {
      await navigator.clipboard.writeText(resetPasswordResult);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      alert("コピーに失敗しました");
    }
  };

  const handleEdit = (person: RegisteredPerson) => {
    setFormData({
      name: person.name,
      gender: person.gender,
      contactNumber: person.contactNumber,
      email: person.email,
      password: person.password,
      tantosha: person.tantosha ?? "",
      genba: person.genba ?? "",
    });
    setEditingId(person.id);
    setResetPasswordResult(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("この登録を削除しますか？")) return;
    try {
      await deleteRegisteredEmployee(id);
      setRegisteredPersons((prev) => prev.filter((p) => p.id !== id));
      if (onDelete) onDelete(id);
      if (editingId === id) handleClear();
    } catch (err) {
      alert(err instanceof Error ? err.message : "削除に失敗しました");
    }
  };

  const handleClose = () => {
    handleClear();
    onClose();
  };

  return (
    <div className="new-reg-modal-overlay" onClick={handleClose}>
      <div className="new-reg-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="new-reg-modal__header">
          <h2 className="new-reg-modal__title">
            <img
              src={addNewIcon}
              alt=""
              className="new-reg-modal__title-icon"
              aria-hidden
            />
            新規登録
          </h2>
          <button
            className="new-reg-modal__close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Form Section */}
           <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="new-reg-modal__form-section">
          <div className="new-reg-modal__form-row">
            <div className="new-reg-modal__form-group">
              <label>氏名</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="氏名を入力"
              />
            </div>
            <div className="new-reg-modal__form-group">
              <label>性別</label>
              <select
                value={formData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
              >
                <option value="">選択してください</option>
                <option value="male">男性</option>
                <option value="female">女性</option>
              </select>
            </div>
            <div className="new-reg-modal__form-group">
              <label>連絡先番号</label>
              <input
                type="tel"
                value={formData.contactNumber}
                onChange={(e) =>
                  handleInputChange("contactNumber", e.target.value)
                }
                placeholder="連絡先番号を入力"
              />
            </div>
          </div>

          <div className="new-reg-modal__form-row">
            <div className="new-reg-modal__form-group">
              <label>メールアドレス</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="メールアドレスを入力"
              />
            </div>
            <div className="new-reg-modal__form-group">
              <label>
                担当者 <span className="new-reg-modal__optional">(任意)</span>
              </label>
              <input
                type="text"
                value={formData.tantosha}
                onChange={(e) => handleInputChange("tantosha", e.target.value)}
                placeholder="担当者を入力"
              />
            </div>
            <div className="new-reg-modal__form-group">
              <label>
                現場名 <span className="new-reg-modal__optional">(任意)</span>
              </label>
              <input
                type="text"
                value={formData.genba}
                onChange={(e) => handleInputChange("genba", e.target.value)}
                placeholder="現場名を入力"
              />
            </div>
          </div>

          {editingId ? (
            <div className="new-reg-modal__reset-pwd-section">
              {resetPasswordResult ? (
                <div className="new-reg-modal__reset-pwd-result">
                  <span className="new-reg-modal__reset-pwd-label">新しいパスワード:</span>
                  <span className="new-reg-modal__reset-pwd-value">{resetPasswordResult}</span>
                  <button
                    type="button"
                    className="new-reg-modal__btn new-reg-modal__btn--copy"
                    onClick={handleCopyPassword}
                  >
                    {copyFeedback ? "コピーしました" : "コピー"}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="new-reg-modal__btn new-reg-modal__btn--reset-pwd"
                  onClick={handleResetPassword}
                  disabled={isResettingPassword}
                >
                  {isResettingPassword ? "リセット中..." : "パスワードをリセット"}
                </button>
              )}
            </div>
          ) : (
            <p className="new-reg-modal__password-note">
              パスワードは招待メール送信時にシステムで自動発行され、メールでお知らせします。
            </p>
          )}

          {/* Action Buttons */}
          <div className="new-reg-modal__actions">
            <button
              type="submit"
              className="new-reg-modal__btn new-reg-modal__btn--save"
              disabled={isSubmitting}
            >
              {editingId ? "更新" : "保存"}
            </button>
            <button
              type="button"
              className="new-reg-modal__btn new-reg-modal__btn--clear"
              onClick={handleClear}
            >
              クリア
            </button>
          </div>
        </div>
        </form>

        {/* Registered Persons Table */}
        <div className="new-reg-modal__table-section">
          <h3 className="new-reg-modal__table-title">登録済み一覧</h3>
          <div className="new-reg-modal__table-container">
            <table className="new-reg-modal__table">
              <thead>
                <tr>
                  <th>番号</th>
                  <th>氏名</th>
                  <th>性別</th>
                  <th>連絡先番号</th>
                  <th>メールアドレス</th>
                  <th>担当者</th>
                  <th>現場名</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {registeredPersons.length > 0 ? (
                  registeredPersons.map((person, index) => (
                    <tr key={person.id}>
                      <td>{index + 1}</td>
                      <td>{person.name}</td>
                      <td>{person.gender === "male" ? "男性" : "女性"}</td>
                      <td>{person.contactNumber}</td>
                      <td>{person.email}</td>
                      <td>{person.tantosha || "—"}</td>
                      <td>{person.genba || "—"}</td>
                      <td>
                        <div className="new-reg-modal__actions-cell">
                          <button
                            className="new-reg-modal__action-btn new-reg-modal__action-btn--edit"
                            onClick={() => handleEdit(person)}
                            aria-label="Edit"
                          >
                            <img src={editIcon} alt="" aria-hidden />
                          </button>
                          <button
                            className="new-reg-modal__action-btn new-reg-modal__action-btn--delete"
                            onClick={() => handleDelete(person.id)}
                            aria-label="Delete"
                          >
                            <img src={eraserIcon} alt="" aria-hidden />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="new-reg-modal__empty">
                      登録データがありません
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewRegistrationModal;
